import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, fmtDT, num } from "@/lib/format";
import { Loading } from "@/components/common/kit";
import IssueSlip from "@/components/common/IssueSlip";

const TYPE_LABEL = { OPENING: "Initial opening", ISSUE: "Stock issued", TRANSFER_IN: "Transfer in", TRANSFER_OUT: "Transfer out", RETURN: "Returned", ADJUSTMENT: "Adjustment", SALE: "Sold (day closure)", SALE_REVERSAL: "Sale reversed (reopen)" };

export default function MyStock() {
  const { salesman } = useAuth();
  const { data: stock } = useFetch("/stock/current", { salesman_id: salesman.id });
  const { data: issues } = useFetch("/issues", { limit: 10 });
  const { data: ledger } = useFetch("/ledger", { limit: 20 });
  const { data: products } = useFetch("/products");
  const [slip, setSlip] = useState(null);
  if (!stock) return <Loading />;
  const pname = Object.fromEntries((products || []).map((p) => [p.id, p.name]));
  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-bold tracking-tight">My Stock</h1>
        <p className="font-mono text-sm">Total <b data-testid="my-stock-total">{num(stock.total)}</b> pks</p>
      </div>
      <div className="space-y-2" data-testid="my-stock-list">
        {stock.items.map((s) => (
          <div key={s.product_id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`my-stock-${s.sku}`}>
            <div><p className="font-semibold">{s.name}</p><p className="text-xs text-slate-500">{s.sku}{s.pending_out > 0 && ` · ${s.pending_out} pending out`}</p></div>
            <p className="font-mono text-2xl font-bold" data-testid={`my-stock-qty-${s.sku}`}>{s.qty}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Stock issued to me</p>
        <div className="space-y-2">
          {(issues?.items || []).map((i) => (
            <button key={i.id} onClick={() => setSlip(i)} className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3 text-left" data-testid={`my-issue-${i.issue_no}`}>
              <div><p className="font-mono text-sm font-semibold">{i.issue_no}</p><p className="text-xs text-slate-500">{fmtDate(i.date)} · {i.items.length} product(s)</p></div>
              <p className="font-mono font-bold">+{i.total_packs}</p>
            </button>
          ))}
          {issues?.items?.length === 0 && <p className="text-sm text-slate-500">No issues yet.</p>}
        </div>
      </div>
      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Recent stock movements</p>
        <div className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
          {(ledger?.items || []).map((l) => (
            <div key={l.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div><p className="font-medium">{TYPE_LABEL[l.type]} · {pname[l.product_id]}</p><p className="text-xs text-slate-500">{fmtDate(l.date)} · {fmtDT(l.created_at)}</p></div>
              <span className={`font-mono font-bold ${l.qty < 0 ? "text-red-600" : "text-emerald-700"}`}>{l.qty > 0 ? "+" : ""}{l.qty}</span>
            </div>
          ))}
        </div>
      </div>
      <IssueSlip issue={slip} onClose={() => setSlip(null)} />
    </div>
  );
}
